import React from 'react';
import { Platform, View, Text, StyleSheet } from 'react-native';
import Svg, { Path, Line, Rect } from 'react-native-svg';
import { AdminColors } from '../../constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import { useTheme } from '../../../../theme/useTheme';

interface EmptyStateProps {
  title?: string;
  subtitle?: string;
}

const EmptyIcon = ({ isDark, bgColor, lineColor }: { isDark: boolean; bgColor: string; lineColor: string }) => (
  <Svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    {/* Document outline */}
    <Rect x="6" y="4" width="24" height="32" rx="3" stroke={lineColor} strokeWidth="1.8" fill={bgColor} />
    {/* Folded corner */}
    <Path d="M24 4 L30 10 L24 10 Z" stroke={lineColor} strokeWidth="1.8" fill={isDark ? bgColor : AdminColors.nearWhite} strokeLinejoin="round" />
    {/* Lines representing content */}
    <Line x1="11" y1="17" x2="25" y2="17" stroke={lineColor} strokeWidth="1.8" strokeLinecap="round" />
    <Line x1="11" y1="22" x2="25" y2="22" stroke={lineColor} strokeWidth="1.8" strokeLinecap="round" />
    <Line x1="11" y1="27" x2="19" y2="27" stroke={lineColor} strokeWidth="1.8" strokeLinecap="round" />
  </Svg>
);

const EmptyState: React.FC<EmptyStateProps> = ({
  title = 'No Content Configured',
  subtitle = 'Please Create a New Item',
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  return (
    <View style={styles.container}>
      <View
        style={[
          styles.iconWrapper,
          isDark && {
            backgroundColor: colors.background.elevated,
            borderColor: colors.border.default,
          },
        ]}
      >
        <EmptyIcon
          isDark={isDark}
          bgColor={isDark ? colors.background.elevated : AdminColors.bgPage}
          lineColor={isDark ? colors.text.disabled : AdminColors.gray150}
        />
      </View>
      <Text style={[styles.title, isDark && { color: colors.text.primary }]}>{title}</Text>
      <Text style={[styles.subtitle, isDark && { color: colors.text.disabled }]}>{subtitle}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: 56,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconWrapper: {
    width: 64,
    height: 64,
    borderRadius: 12,
    backgroundColor: AdminColors.bgPage,
    borderWidth: 1,
    borderColor: AdminColors.gray50,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 1,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.medium,
    marginBottom: 6,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 13,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
    textAlign: 'center',
  },
});

export default EmptyState;
